import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { apiRoute } from '../../../../constants/api-constants';
import { colors } from '../../../../style-utils/colors';
import { palette, status } from '../../../../style-utils/theme';
import {
  Presence,
  Staleable,
  StateSwitch,
  overlayMotion,
} from '../../../../motion';
/* WP-37 — the shared table engine, replacing this screen's percentage-width
   flex divs. See the module header for the mobile contract it inherits. */
import {
  BodyRow,
  CellFlex,
  Table,
  TableCaption,
  TableFrame,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../../table/list-table';
import { Text } from '../../../../style-utils/text';
import {
  ActionModal,
  UsersListing,
  ButtonContainer,
  ButtonTitle,
  Column,
  DashboardContent,
  NewUserButton,
  RestoreButton,
  RowActionTrigger,
  Title,
  RoleDiv,
  ActionModalBackground,
  ViewPropertiesButton,
  PropertiesModal,
  Avatar,
  AvatarFallback,
  AvatarImg,
  EmptyState,
  EmptyStateIcon,
  TypeFilterLabel,
  TypeFilterSelect,
} from './dashboard-users.styled';
import { useNavigate } from 'react-router-dom';
import { ThreeDots } from '../../../icons/three-dots';
import { PaginationButtons } from '../dashboard-shared-components/table-pagination/table-pagination';
import { usePagination } from '../../../hooks/use-pagination';
import {
  createItem,
  fetchAllItems,
  updatePropertyOrUserItem,
} from '../../../requests/requests';
import {
  User,
  UserWithBuildingsObjects,
} from '../../../../interfaces/user-interfaces';
import { useAuth } from '../../../../context/auth-provider';
import { useResource } from '../../../hooks/use-resource';
import { PendingInvitations } from './pending-invitations';
import {
  ListSkeleton,
  LoadError,
  skeletonHeight,
} from '../dashboard-shared-components/load-state';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  ROLES,
  normalizeRole,
  outranks,
  roleLabel,
} from '../../../../auth/roles';
import { RightArrowBlack } from '../../../icons/right-arrow-black';
import { BuildingData } from '../../../../interfaces/building-interfaces';
import { ListingControls, ListingSearch } from '../dashboard.styled';
import { StyledInput } from '../../../login/login.styled';
import toast from '../../../../utils/toast';
import { deskInPlan } from '../../../../utils/products';

interface DashboardUsersProps {
  updateEditableUserData: (newValue: User) => void;
  /**
   * WP-26. Bumped by the parent when the add-user modal saves.
   *
   * Adding a user is a MODAL OVER THE LIST: the route returns an unkeyed
   * fragment holding both this component and the form, so React reconciles by
   * type at index 0 and REUSES this fiber. It never unmounts, useResource's
   * mount effect never fires again, and the list a person is looking at does
   * not contain the user they just created. The edit route returns the editor
   * ALONE, which is the only reason editing appeared to work.
   */
  refreshToken?: number;
}

/* The list's rows are the table's own height; the skeleton reserves it. */
const SKELETON_ROWS = 5;
// Measured on the rendered table at 1366px: the avatar row.
const ROW_HEIGHT = 92;

const getInitials = (name?: string): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

  return (first + last).toUpperCase();
};

export const DashboardUsers: React.FC<DashboardUsersProps> = ({
  updateEditableUserData,
  refreshToken = 0,
}) => {
  const {
    user,
    permissions,
  }: { user: User | null | undefined; permissions: string[] } = useAuth();
  const canCreateUser = can(permissions, PERMISSIONS.USER_CREATE);
  const canEditUser = can(permissions, PERMISSIONS.USER_EDIT);
  // ── THE SERVER'S RULE, MIRRORED SO THE ROW DOES NOT OFFER A DEAD END ──────
  // `user:edit` used to mean "you are the owner, act on anybody". It now means
  // "you may administer staff", and WHICH staff is decided per row by
  // assertMayAdminister in lobbero_be/src/CRUD/users.js: never yourself, never
  // an Owner, and only someone strictly below you.
  //
  // MIRRORED, NOT ENFORCED HERE. The server refuses these regardless — this
  // only stops the menu offering a manager an Edit that answers 409 on an Owner
  // row and 403 on a peer's, which reads as a broken screen rather than a rule.
  const actorRole = normalizeRole(user?.role);
  const mayAdminister = (row: UserWithBuildingsObjects): boolean =>
    String(row.id) !== String(user?.id ?? '') &&
    normalizeRole(row.role) !== ROLES.OWNER &&
    outranks(actorRole, normalizeRole(row.role));

  const [editableUserId, setEditableUserId] = useState<string | null>(null);
  const [userToBeDeleted, setUserToBeDeleted] = useState<User | null>(null);
  const [isActionModalVisible, setIsActionModalVisible] =
    useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);
  const [isPropertiesModalVisible, setIsPropertiesModalVisible] =
    useState<boolean>(false);
  const propertiesModalRef = useRef<HTMLDivElement | null>(null);

  const [userParam, setUserParam] = useState('');
  // eslint-disable-next-line
  const handleSearchChange = (event: any) => {
    setUserParam(event.target.value);
  };

  // WHAT KIND OF PERSON (WP-14 / BUG-010). The payload already carries `role`
  // and `is_resident` on every row and the whole list is already downloaded, so
  // this is a client-side narrowing and needs nothing from the API.
  const [typeFilter, setTypeFilter] = useState<
    'all' | 'owner' | 'property_manager' | 'concierge' | 'resident'
  >('all');

  /* ── THE INACTIVE VIEW (QA-09, owner decision 2026-08-28) ──────────────────
   *
   * Deleting a person here is a SOFT delete — the row, its history and its audit
   * trail all survive — but there was no way back from inside the product, so a
   * mis-click became a support ticket. This is the way back.
   *
   * A SEPARATE VIEW, not another value on the Type filter. Two reasons, and both
   * were worth the extra control: mixing live colleagues with deleted ones puts
   * a Restore button one row away from people it means nothing for, and the two
   * lists come from DIFFERENT requests — `?status=inactive` is a server-side
   * narrowing, because the People route has always returned active accounts only
   * and widening it for everybody would change what every existing caller sees.
   */
  const [lifecycle, setLifecycle] = useState<'active' | 'inactive'>('active');
  // Which row is mid-restore, so its own button can say so and the others stay
  // pressable. A boolean would disable the whole table on one click.
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // TRUE WHEN THE BOX HAS SOMETHING IN IT. It used to be the exact inverse, and
  // a comment beside the empty state claimed the resulting copy had been
  // corrected — it had not, and the two faults cancelled nowhere: a search with
  // no hits told a populated organisation "No users yet. Users you add will
  // appear here."
  const isSearching = Boolean(userParam && userParam.trim().length > 0);
  /**
   * THE PEOPLE LIST — through useResource, for the same reason Buildings is.
   *
   * This was a bare useEffect with no try/catch and no loading flag; the spinner
   * that used to wrap the table is commented out at two places in this file. So
   * before the request resolved, and permanently after it rejected, the screen
   * rendered its empty state — an org with sixty staff was told it had none,
   * with no error and no way to retry.
   */
  const users = useResource<UserWithBuildingsObjects[]>(
    async () => {
      if (!user) return [];

      // The inactive view is owner-only and server-narrowed. A non-owner takes
      // the POST branch, which has no status parameter — so `lifecycle` is only
      // ever 'inactive' for a caller that can actually ask for it, and the
      // control below is not rendered for anybody else.
      const response =
        normalizeRole(user?.role) === ROLES.OWNER
          ? await fetchAllItems(
              'dashboard/users',
              undefined,
              lifecycle === 'inactive' ? { status: 'inactive' } : undefined
            )
          : await createItem('dashboard/users', user);

      return (response || []) as UserWithBuildingsObjects[];
    },
    {
      enabled: Boolean(user),
      fallbackMessage: 'We could not load your people.',
      // Re-runs when the view changes: the two lists are different REQUESTS,
      // not two filters over one payload.
      deps: [lifecycle],
    }
  );

  const usersData = users.data || [];
  const getUsersfromDB = users.reload;

  // Refetch when the parent says something was created. Guarded on the value
  // rather than fired on mount: useResource already loads once, and an
  // unguarded effect would double the request on every visit to this screen.
  const seenRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (seenRefreshToken.current === refreshToken) return;
    seenRefreshToken.current = refreshToken;
    void getUsersfromDB();
  }, [refreshToken, getUsersfromDB]);

  // ONE DERIVED LIST (WP-14). This was a `filteredUsers` state written by a
  // `filterUsers(term)` called from the change handler, and it had two faults
  // that a derived value simply cannot have: the handler read `isSearching`
  // from the previous render, so the first keystroke set the list to EVERY
  // user; and the state could drift from `usersData` whenever the list
  // reloaded underneath it without a keystroke.
  const visibleUsers = useMemo(() => {
    const term = String(userParam || '').trim().toLowerCase();

    return usersData.filter(row => {
      // TYPE. Residency is not a role since migration 037, so a resident is
      // identified by the flag the API already sends rather than by `role`,
      // which is null for them.
      if (typeFilter !== 'all') {
        const type = row.is_resident ? 'resident' : normalizeRole(row.role);

        if (type !== typeFilter) return false;
      }

      if (!term) return true;

      // EVERY FIELD DEFENSIVELY. This list is not staff-only — residents are in
      // it — and a resident invited without a phone number has a null here, on
      // which `.includes` throws and takes the whole search with it. The unit is
      // searchable too, because "who is in 4B" is what people type into a
      // people list.
      const matchesPerson =
        String(row.full_name || '').toLowerCase().includes(term) ||
        String(row.email || '').toLowerCase().includes(term) ||
        String(row.phone_number || '').includes(term) ||
        String(row.residency?.unit_number || '').toLowerCase().includes(term);

      const matchesBuilding = row?.propertiesObjects?.some(
        // eslint-disable-next-line
        (property: any) =>
          String(property?.property_name || '').toLowerCase().includes(term)
      );

      return matchesPerson || !!matchesBuilding;
    });
  }, [usersData, userParam, typeFilter]);

  const {
    currentPage,
    displayedData,
    nextPage,
    previousPage,
    hasPreviousPage,
    hasNextPage,
    getTotalPages,
    resetPagination,
  } = usePagination(visibleUsers);

  const totalPages = getTotalPages();
  const navigate = useNavigate();

  // Page 4 of a list that just became one page long is an empty screen that
  // looks like "no results". Any change to the narrowing goes back to page 1.
  // Deliberately keyed on the narrowing only. `resetPagination` is not in the
  // deps because it is a fresh identity on every render of usePagination, and
  // depending on it would reset the page on every render instead of on every
  // filter change.
  // eslint-disable-next-line
  useEffect(() => {
    resetPagination();
  }, [userParam, typeFilter]);

  const openAddNewUserForm = () => {
    navigate('/dashboard/users/add-new-user');
  };



  const showEditDeleteActions = (userId: string) => {
    setIsActionModalVisible(!isActionModalVisible);

    setEditableUserId(userId);

    const filteredUserById = usersData?.filter(user => user.id === userId);

    setUserToBeDeleted(filteredUserById[0] as User);
    updateEditableUserData(filteredUserById[0] as User);
  };

  /* RESET TWO-STEP SIGN-IN — for a colleague who lost both their phone and
     their recovery codes. Same rank rule as delete (the menu only renders for
     people you may administer); the server signs them out everywhere and
     tells them. */
  const [mfaResetTarget, setMfaResetTarget] = useState<User | null>(null);
  const [mfaResetting, setMfaResetting] = useState(false);

  const handleMfaResetClick = () => {
    setMfaResetTarget(userToBeDeleted);
    setIsActionModalVisible(false);
  };

  const handleConfirmMfaReset = async () => {
    if (!mfaResetTarget || mfaResetting) return;
    const who = mfaResetTarget.full_name || 'This person';

    setMfaResetting(true);
    try {
      const { data } = await axios.post(
        `${apiRoute}/dashboard/users/${mfaResetTarget.id}/mfa-reset`,
        {}
      );

      toast.success(
        data.had_two_step
          ? `Two-step sign-in reset for ${who}. They were signed out and can set it up again.`
          : `${who} did not have two-step sign-in. They were signed out.`
      );
      setMfaResetTarget(null);
    } catch (error) {
      toast.error(
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message || 'Could not reset two-step sign-in. Please try again.'
      );
    } finally {
      setMfaResetting(false);
    }
  };

  const hideEditDeleteActions = () => {
    setIsActionModalVisible(false);
    setEditableUserId(null);
    setUserToBeDeleted(null);
  };

  const handleDeleteButtonClick = () => {
    setIsDeleteModalVisible(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
  };

  const handleConfirmDelete = async () => {
    // A MISSING TARGET IS A FAULT, NOT A NO-OP — the same hardening the
    // Buildings screen needed for QA-01. This screen has no outside-click
    // listener to lose the target to, so it was not affected by that bug; the
    // guard is here because "if (target) { …request… }" followed by an
    // unconditional refetch and a clean close SILENTLY REPORTS SUCCESS on a
    // destructive action, and that shape must not survive anywhere.
    if (!userToBeDeleted) {
      toast.error('Could not tell which person to delete. Please try again.');

      return;
    }

    const deletedName = userToBeDeleted.full_name || 'That person';

    try {
      await updatePropertyOrUserItem('dashboard/users', {
        ...userToBeDeleted,
        status: 'inactive',
      });

      await getUsersfromDB();
      setIsDeleteModalVisible(false);
      setUserToBeDeleted(null);
      // Names the consequence, not just the act: the grants go with the person
      // (QA-02), and a restore does not bring them back.
      toast.success(`${deletedName} deleted. Their building access was revoked.`);
    } catch {
      toast.error('Could not delete that user. Please try again.');
    }
  };

  /* ── RESTORE (QA-09) ───────────────────────────────────────────────────────
   *
   * The counterpart to the soft delete above. Deliberately NOT a silent inverse:
   * deactivating revoked the person's grants and restoring does not re-mint them
   * — there is no record of what was revoked that could be replayed safely, and
   * quietly handing an org-wide grant back to a reactivated account is the worst
   * available way to be wrong. The toast says so, because the screen is the only
   * place the reader will learn it.
   */
  const handleRestore = async (person: User) => {
    if (restoringId) return;
    setRestoringId(person.id);
    try {
      await updatePropertyOrUserItem('dashboard/users', {
        id: person.id,
        status: 'active',
      });
      await getUsersfromDB();
      toast.success(
        `${person.full_name || 'That person'} restored. Give them building access to let them work.`
      );
    } catch {
      toast.error('Could not restore that person. Please try again.');
    } finally {
      setRestoringId(null);
    }
  };

  const handleViewProperties = (userId: string) => {
    setIsPropertiesModalVisible(!isPropertiesModalVisible);
    setEditableUserId(userId);

    const filteredUserById = usersData?.filter(user => user.id === userId);

    updateEditableUserData(filteredUserById[0] as User);
  };

  useEffect(() => {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any) {
      if (
        propertiesModalRef.current &&
        !propertiesModalRef.current.contains(event.target)
      ) {
        // Clicked outside the container, so close it
        setIsPropertiesModalVisible(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <DashboardContent id="dashboard-users-content">
      <UsersListing>
        <Title>
          <Text mt={4} mb={0} fontSize={18} fontWeight={700}>
            Users
          </Text>
          <Text mt={4} mb={0} fontSize={12} color={`${colors.grey}`}>
            {/* The server bounds this list to the buildings the caller manages
                (owner decision Q-3). Saying so matters: a manager who used to
                see the whole organisation would otherwise read the shorter list
                as people having disappeared. Owners are not scoped, so they get
                the original line. */}
            {actorRole === ROLES.OWNER
              ? 'Please select user to show'
              : 'Showing people in the buildings you manage'}
          </Text>
        </Title>
        {/* ONE GROUP, so the row wraps under the title instead of squeezing
            it (dashboard.styled.tsx, ListingControls). */}
        <ListingControls>
          <ListingSearch>
            {/* An accessible name, and the RIGHT one. The only text describing
                this box was a placeholder — which vanishes the moment anything is
                typed, and is not a label — above a commented-out "Search:" that
                had been dead for long enough to be forgotten. The users list's box
                also carried name="userSearch". */}
            <StyledInput
              name="userSearch"
              type="search"
              aria-label="Search people"
              value={userParam}
              onChange={handleSearchChange}
              placeholder="Search..."
            />
          </ListingSearch>
          {/* TYPE (WP-14 / BUG-010). Owner, manager, concierge or resident.
              Labelled, not placeholder-only: a placeholder disappears the moment
              a value is chosen, and this control's whole job is to say what the
              screen is currently hiding. */}
          {/* ACTIVE / DELETED (QA-09). Owner-only, because the inactive view is a
              server-side narrowing only the owner's GET branch can ask for — a
              manager takes the POST branch, which has no status parameter, so
              rendering the control for them would move a toggle that changes
              nothing. */}
          {normalizeRole(user?.role) === ROLES.OWNER && (
            <TypeFilterLabel>
              <span>Show</span>
              <TypeFilterSelect
                aria-label="Show active or deleted people"
                value={lifecycle}
                onChange={event =>
                  setLifecycle(event.target.value as 'active' | 'inactive')
                }
              >
                <option value="active">Active people</option>
                <option value="inactive">Deleted people</option>
              </TypeFilterSelect>
            </TypeFilterLabel>
          )}
          <TypeFilterLabel>
            <span>Type</span>
            <TypeFilterSelect
              aria-label="Filter people by type"
              value={typeFilter}
              onChange={event =>
                setTypeFilter(
                  event.target.value as
                    | 'all'
                    | 'owner'
                    | 'property_manager'
                    | 'concierge'
                    | 'resident'
                )
              }
            >
              <option value="all">All people</option>
              <option value="owner">{roleLabel(ROLES.OWNER)}</option>
              <option value="property_manager">
                {roleLabel(ROLES.PROPERTY_MANAGER)}
              </option>
              {/* No concierges or residents without Lobbero Desk
                  (Independent Stays IS1, R-IS1.10). */}
              {deskInPlan(user) && (
                <option value="concierge">{roleLabel(ROLES.CONCIERGE)}</option>
              )}
              {deskInPlan(user) && <option value="resident">Resident</option>}
            </TypeFilterSelect>
          </TypeFilterLabel>
          {canCreateUser && (
            <NewUserButton
              id="new-user-button"
              onClick={() => openAddNewUserForm()}
            >
              Add new user
            </NewUserButton>
          )}
        </ListingControls>
      </UsersListing>

      <Column>
        {/* ONE BOX, FOUR FILLS. The table, the skeleton, the error and the
            empty state used to be four siblings that appeared and vanished
            around each other; StateSwitch crossfades them inside one container
            with a reserved floor. A refetch (isRefreshing — the filter, a
            restore, a delete) is not a state change: the table stays and
            Staleable dims it until the new rows land. */}
        <StateSwitch
          state={
            users.isLoading
              ? 'loading'
              : users.error
                ? 'error'
                : displayedData.length === 0
                  ? 'empty'
                  : 'ready'
          }
          minHeight={skeletonHeight(SKELETON_ROWS, ROW_HEIGHT)}
        >
          {users.isLoading ? (
            <ListSkeleton
              rows={SKELETON_ROWS}
              rowHeight={ROW_HEIGHT}
              label="people"
            />
          ) : users.error ? (
            <LoadError message={users.error} onRetry={users.reload} />
          ) : displayedData.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>&#128100;</EmptyStateIcon>
              <Text fontSize={18} fontWeight={700} mb={0} color={palette.ink}>
                {isSearching ? 'No matching users' : 'No users yet'}
              </Text>
              <Text fontSize={14} mt={0} mb={0} color={palette.slate}>
                {isSearching
                  ? 'Try a different name, email, phone, or property.'
                  : 'Users you add will appear here.'}
              </Text>
            </EmptyState>
          ) : (
            <Staleable stale={users.isRefreshing}>
              <TableFrame>
                <Table>
                  <TableCaption>
                    People with access to this organisation, the buildings they
                    cover and how to reach them
                  </TableCaption>
                  <Thead>
                    <Tr>
                      <Th width={20}>User name</Th>
                      <Th width={20}>Property</Th>
                      <Th width={20}>Email</Th>
                      <Th width={18}>Phone</Th>
                      <Th width={14}>Role</Th>
                      {canEditUser && <Th width={8}>Action</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* ROWS ARRIVE IN A CASCADE AND LEAVE WHEN DELETED. Keyed by
                        id inside a Presence: a deleted person animates out and
                        the rows under them slide up. The Presence is keyed by
                        page and filter so a page turn or a search swaps the set
                        instantly and replays the cascade instead of stacking
                        two pages of rows. */}
                    <Presence
                      key={`${currentPage}:${userParam}:${typeFilter}:${lifecycle}`}
                    >
                      {displayedData.map((e, i) => {
                        // Rows come from the API, which may still carry a
                        // pre-rename value.
                        const role = normalizeRole(e.role);

                        return (
                          <BodyRow key={e.id} index={i}>
                            <Td data-label="User name">
                              <CellFlex>
                                <Avatar>
                                  {e.user_image ? (
                                    <AvatarFallback>
                                      <AvatarImg
                                        src={e.user_image as string}
                                        alt=""
                                      />
                                    </AvatarFallback>
                                  ) : (
                                    <AvatarFallback>
                                      {getInitials(
                                        e.full_name ||
                                          e.propertiesObjects[0]?.property_name
                                      )}
                                    </AvatarFallback>
                                  )}
                                </Avatar>

                                <Text
                                  mt={0}
                                  mb={0}
                                  fontSize={14}
                                  fontWeight={500}
                                  color={colors.grey_dark}
                                >
                                  {e.full_name
                                    ? `${e.full_name}`
                                    : e.propertiesObjects[0]?.property_name &&
                                      `${e.propertiesObjects[0].property_name}`}
                                </Text>
                              </CellFlex>
                            </Td>

                            <Td data-label="Property" style={{ cursor: 'pointer' }}>
                              {/* NO DOOR TO AN EMPTY ROOM. The button opened a
                                  popover that rendered nothing for anybody with
                                  no buildings — a control that looks broken
                                  rather than one that says "none". The count is
                                  in the label too: it is the answer most people
                                  wanted from the click. */}
                              {e.propertiesObjects?.length ? (
                                <ViewPropertiesButton
                                  type="button"
                                  aria-expanded={
                                    isPropertiesModalVisible &&
                                    editableUserId === e.id
                                  }
                                  aria-controls={`properties-${e.id}`}
                                  onClick={() => handleViewProperties(e.id)}
                                >
                                  <Text
                                    as="span"
                                    mt={0}
                                    mb={0}
                                    mr={8}
                                    fontSize={14}
                                    fontWeight={600}
                                    color={palette.irisDark}
                                  >
                                    {e.propertiesObjects.length === 1
                                      ? 'View 1 property'
                                      : `View ${e.propertiesObjects.length} properties`}
                                  </Text>
                                  <RightArrowBlack />
                                </ViewPropertiesButton>
                              ) : (
                                <Text
                                  as="span"
                                  mt={0}
                                  mb={0}
                                  fontSize={14}
                                  fontWeight={400}
                                  color={colors.grey_dark}
                                >
                                  No buildings
                                </Text>
                              )}
                              {/* The popover grows from the button and shrinks
                                  back (Presence + 'popover'); the transparent
                                  catcher behind it needs no motion and leaves
                                  the instant the Presence lets go of it. */}
                              <Presence>
                                {e.propertiesObjects?.length &&
                                isPropertiesModalVisible &&
                                editableUserId === e.id ? (
                                  <ActionModalBackground
                                    key="properties-bg"
                                    id="properties-modal-background"
                                    aria-hidden="true"
                                    onClick={() =>
                                      setIsPropertiesModalVisible(false)
                                    }
                                  />
                                ) : null}
                                {e.propertiesObjects?.length &&
                                isPropertiesModalVisible &&
                                editableUserId === e.id ? (
                                  <PropertiesModal
                                    key="properties"
                                    {...overlayMotion('popover')}
                                    id={`properties-${e.id}`}
                                    role="region"
                                    aria-label={`Properties for ${e.full_name}`}
                                  >
                                    {e.propertiesObjects.map(
                                      (property: BuildingData) => (
                                        <Text
                                          key={property.id}
                                          mt={0}
                                          mb={0}
                                          fontSize={14}
                                          fontWeight={400}
                                          color={colors.grey_dark}
                                        >
                                          {property.property_name}
                                        </Text>
                                      )
                                    )}
                                  </PropertiesModal>
                                ) : null}
                              </Presence>
                            </Td>
                            <Td data-label="Email">
                              <Text
                                mt={0}
                                mb={0}
                                fontSize={14}
                                fontWeight={400}
                                color={colors.grey_dark}
                              >
                                {e.email}
                              </Text>
                            </Td>

                            <Td data-label="Phone">
                              <Text
                                mt={0}
                                mb={0}
                                fontSize={14}
                                fontWeight={400}
                                color={colors.grey_dark}
                              >
                                {e.phone_number}
                              </Text>
                            </Td>

                            <Td data-label="Role">
                              <RoleDiv
                                isManager={role === ROLES.PROPERTY_MANAGER}
                                isAdmin={role === ROLES.OWNER}
                              >
                                <Text
                                  mt={0}
                                  mb={0}
                                  fontSize={12}
                                  fontWeight={600}
                                  color={
                                    role === ROLES.OWNER
                                      ? status.brand.fg
                                      : status.neutral.fg
                                  }
                                >
                                  {/* `roleLabel(null)` is "Unknown", which is
                                      what every tenant row would have said:
                                      since migration 037 a resident holds no
                                      staff role, so the server sends the flag
                                      and this reads it. A former resident keeps
                                      the label and loses the unit. */}
                                  {e.is_resident
                                    ? e.residency?.unit_number
                                      ? `Resident · ${e.residency.unit_number}`
                                      : 'Resident'
                                    : roleLabel(e.role)}
                                </Text>
                              </RoleDiv>
                            </Td>

                            {/* NO STAFF ROW-ACTIONS ON A RESIDENT. The menu
                                behind this changes a role and reassigns
                                buildings — neither of which is how a resident's
                                access works. Moving somebody in or out of a
                                unit is the roster's job, and offering it here
                                would be a second, subtly different way to do
                                it. */}
                            {/* A DELETED ROW GETS ONE ACTION, AND IT IS NOT THE
                                MENU. Edit and Delete are meaningless on
                                somebody who cannot sign in; offering them
                                would be the same misleading affordance QA-02
                                found on Staff Access. */}
                            {lifecycle === 'inactive' &&
                              canEditUser &&
                              mayAdminister(e) && (
                                <Td data-label="Action">
                                  <RestoreButton
                                    type="button"
                                    disabled={restoringId === e.id}
                                    aria-label={`Restore ${e.full_name}`}
                                    onClick={() => handleRestore(e as User)}
                                  >
                                    {restoringId === e.id
                                      ? 'Restoring…'
                                      : 'Restore'}
                                  </RestoreButton>
                                </Td>
                              )}

                            {lifecycle === 'active' &&
                              canEditUser &&
                              !e.is_resident &&
                              mayAdminister(e) && (
                                <Td data-label="Action">
                                  <RowActionTrigger
                                    type="button"
                                    aria-label={`Actions for ${e.full_name}`}
                                    aria-expanded={
                                      isActionModalVisible &&
                                      editableUserId === e.id
                                    }
                                    onClick={() => showEditDeleteActions(e.id)}
                                  >
                                    <ThreeDots />
                                  </RowActionTrigger>

                                  {/* THE MENU GROWS FROM ITS TRIGGER AND SHRINKS
                                      BACK: Presence + overlayMotion('popover'),
                                      origin at the top-right corner. The
                                      click-catcher behind it is transparent and
                                      needs no motion. The entrance is a
                                      transform and an opacity, so a Delete
                                      clicked the instant the menu exists lands
                                      on a live button. */}
                                  <Presence>
                                    {isActionModalVisible &&
                                    editableUserId === e.id ? (
                                      <ActionModalBackground
                                        key="menu-bg"
                                        id="action-modal-background"
                                        onClick={() => hideEditDeleteActions()}
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                    {isActionModalVisible &&
                                    editableUserId === e.id ? (
                                      <ActionModal
                                        key="menu"
                                        {...overlayMotion('popover')}
                                        style={{ transformOrigin: 'top right' }}
                                        role="group"
                                        aria-label={`Actions for ${e.full_name}`}
                                      >
                                        <ButtonContainer
                                          type="button"
                                          onClick={handleMfaResetClick}
                                        >
                                          <ButtonTitle>Reset two-step sign-in</ButtonTitle>
                                        </ButtonContainer>
                                        <ButtonContainer
                                          type="button"
                                          isDelete={true}
                                          onClick={handleDeleteButtonClick}
                                        >
                                          <ButtonTitle>Delete</ButtonTitle>
                                        </ButtonContainer>
                                        <ButtonContainer
                                          type="button"
                                          // ABSOLUTE. This screen renders under
                                          // the '/dashboard/*' splat and
                                          // App.tsx turns on
                                          // `v7_relativeSplatPath`, under which
                                          // a relative path resolves against
                                          // the FULL location rather than the
                                          // part before the splat — so from
                                          // /dashboard/users this produced
                                          // /dashboard/users/users/edit-user,
                                          // which the dashboard's switch does
                                          // not answer. Same defect that made
                                          // every account tab dead.
                                          onClick={() =>
                                            navigate('/dashboard/users/edit-user')
                                          }
                                        >
                                          <ButtonTitle>Edit</ButtonTitle>
                                        </ButtonContainer>
                                      </ActionModal>
                                    ) : null}
                                  </Presence>
                                </Td>
                              )}
                          </BodyRow>
                        );
                      })}
                    </Presence>
                  </Tbody>
                </Table>
              </TableFrame>
            </Staleable>
          )}
        </StateSwitch>
        {/* An invitation is a person who is not in the list yet, so it belongs
            in front of the list they are missing from. */}
        <PendingInvitations />

        <PaginationButtons
          currentPage={currentPage}
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          totalPages={totalPages}
        />
      </Column>
      <ConfirmDialog
        open={isDeleteModalVisible}
        title="Delete person"
        /* ONE WORD FOR THE DESTRUCTIVE ACTION (R-14). The row said Delete and
           this dialog said Deactivate — the same click described two ways, so
           whichever the user read, the other was a surprise.
           Underneath it is still a soft delete: this PUTs status: 'inactive'
           and the server only flips that column (lobbero_be/src/CRUD/users.js).
           Standardising the word must not trade away the honesty, so BOTH facts
           stay in the copy: the records are kept, and this cannot be undone
           from inside the app (R-15). The consequence the admin actually needs
           to hear — immediate loss of access — leads. */
        message={
          userToBeDeleted?.full_name
            ? `${userToBeDeleted.full_name} will be deleted and will not be able to sign in. Their records are kept in the audit log. This cannot be undone from inside the app.`
            : 'This person will be deleted and will not be able to sign in. Their records are kept in the audit log. This cannot be undone from inside the app.'
        }
        primaryLabel="Delete"
        primaryVariant="destructive"
        dismissLabel="Cancel"
        onPrimary={handleConfirmDelete}
        onDismiss={handleCancelDelete}
      />
      <ConfirmDialog
        open={mfaResetTarget !== null}
        title="Reset two-step sign-in"
        message={`${mfaResetTarget?.full_name || 'This person'} will be signed out on every device and can sign in with the password alone until they set two-step sign-in up again. Use this only when they have lost both their phone and their recovery codes.`}
        primaryLabel={mfaResetting ? 'Resetting…' : 'Reset'}
        primaryVariant="destructive"
        dismissLabel="Cancel"
        onPrimary={handleConfirmMfaReset}
        onDismiss={() => setMfaResetTarget(null)}
      />
    </DashboardContent>
  );
};
